import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginBrandPanel from './components/LoginBrandPanel';
import LoginForm from './components/LoginForm';
import { collectVisitAttribution } from '../../utils/analytics';
import { recordSiteVisit } from '../../services/waBusinessService';
import PremiumLoader from 'components/ui/PremiumLoader';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithGoogle, resetPasswordForEmail, isAuthenticated, isEmailConfirmed, loading, business, businessLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  useEffect(() => {
    recordSiteVisit({ path: '/login', attribution: collectVisitAttribution() }).catch(() => {});
  }, []);

  // Redirección centralizada tras resolver sesión y negocio.
  useEffect(() => {
    if (loading || !isAuthenticated) return;

    if (!isEmailConfirmed) {
      navigate('/verify-email', { replace: true });
      return;
    }

    // Evita navegar a dashboard sin resolver primero si existe negocio.
    if (businessLoading) return;

    if (business) {
      navigate('/dashboard', { replace: true });
      return;
    }

    navigate('/business-registration', { replace: true });
  }, [isAuthenticated, isEmailConfirmed, loading, navigate, business, businessLoading]);

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const { error } = await signIn(formData?.email, formData?.password);
      if (error) {
        const msg = error?.message?.toLowerCase() || '';
        if (msg?.includes('invalid login') || msg?.includes('invalid credentials') || msg?.includes('wrong') || msg?.includes('incorrect')) {
          setAuthError('Correo o contraseña incorrectos. Intenta nuevamente.');
        } else if (msg?.includes('email not confirmed') || msg?.includes('confirm')) {
          setAuthError('Debes confirmar tu correo antes de iniciar sesión. Revisa tu bandeja de entrada.');
        } else {
          setAuthError(error?.message || 'Error al iniciar sesión. Intenta de nuevo.');
        }
        return;
      }
      // No navegamos aquí para evitar decisiones con estado parcial.
      // El useEffect superior redirige cuando sesión+negocio están resueltos.
    } catch (e) {
      setAuthError('Error inesperado. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    if (!email?.trim()) {
      setAuthError('Ingresa tu correo electrónico para recibir el enlace de recuperación.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setAuthError('Ingresa un correo electrónico válido.');
      return;
    }
    setForgotPasswordLoading(true);
    setAuthError(null);
    try {
      const { error } = await resetPasswordForEmail(email.trim());
      if (typeof window !== 'undefined') {
        console.log('[Login] resetPasswordForEmail result:', error ? { error: error.message } : 'ok');
      }
      if (error) {
        setAuthError(error?.message || 'Error al enviar el correo de recuperación. Intenta de nuevo.');
        return;
      }
      setAuthError(null);
      setForgotPasswordSuccess(true);
    } catch (e) {
      setAuthError('Error inesperado. Por favor intenta de nuevo.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError(null);
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setAuthError(error?.message || 'Error al iniciar sesión con Google.');
        return;
      }
      // Si no hay error, redirige a Google; al volver el callback redirige a dashboard
    } catch (e) {
      setAuthError(e?.message || 'Error inesperado al conectar con Google.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Show nothing while checking auth state
  if (loading) {
    return <PremiumLoader fullScreen text="Preparando tu acceso..." />;
  }

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row lg:flex-row-reverse"
      style={{
        background:
          'linear-gradient(180deg, #fafafa 0%, #ffffff 35%, rgba(245,243,255,0.5) 100%)',
      }}
    >
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 md:px-10 md:py-12 lg:px-14 min-h-0">
        <LoginForm
          onSubmit={handleLogin}
          onGoogleLogin={handleGoogleLogin}
          onForgotPassword={handleForgotPassword}
          isLoading={isLoading}
          googleLoading={googleLoading}
          authError={authError}
          forgotPasswordSuccess={forgotPasswordSuccess}
          forgotPasswordLoading={forgotPasswordLoading}
          onClearForgotSuccess={() => setForgotPasswordSuccess(false)}
          redirectMessage={location?.state?.message}
        />
      </div>
      <LoginBrandPanel />
    </div>
  );
}
